// client/src/coms/personalisation/themeEngine.js
// Turns a theme (built-in or custom, see prefsSchema.js) into CSS custom properties on <html>.
//
// Voxemera's styles use tokens whose fallback is the original dark colour (written by the theme codemod):
//   rgba(var(--vx-fg-rgb, 255,255,255), calc(A * var(--vx-ov, 1)))  overlays, borders and secondary text
//   var(--vx-text, #fff) / var(--vx-text-2, #dfdfdf)                 solid text
//   var(--vxs-N, #original) / rgba(var(--vxs-N-rgb, r,g,b), A)      dark surfaces, N = luminance level 0-7
//   var(--vx-accent…) / var(--vx-link…)                              accent and link families
// The dark theme sets none of them, so it renders exactly as Voxemera always has. Every value written here
// is built by this module from validated hex colours and numbers; nothing from a theme is used as raw CSS.
import { normHex, sanitizeTheme, validateThemeStrict, contrastRatio, themeAccessibility, SCHEMA_VERSION } from './prefsSchema';

// Surface ladder levels as they look in the dark theme (for previews; the dark theme itself sets nothing).
// 0 deepest (server rail) · 1 inputs and cards · 2 bars and menus · 3 app background · 4 between
// 5 panels (channel list, members) · 6 main surface (chat) · 7 raised (hover, borders)
export const DARK_LEVELS = ['#06090f', '#111214', '#18191c', '#1e1f22', '#23252b', '#2b2d31', '#313338', '#3b3c40'];

export const BUILT_IN_THEMES = {
  dark: {
    id: 'dark',
    label: 'Dark',
    base: 'dark',
    colors: { background: '#1e1f22', surface: '#313338', panel: '#2b2d31', text: '#ffffff', textMuted: '#b5bac1', accent: '#5865f2', link: '#8db4ff' },
    levels: DARK_LEVELS,
    ov: 1,
  },
  light: {
    id: 'light',
    label: 'Light',
    base: 'light',
    colors: { background: '#e3e5e8', surface: '#ffffff', panel: '#f2f3f5', text: '#1f2328', textMuted: '#4e5058', accent: '#4752c4', link: '#2456c7' },
    levels: ['#d4d7dc', '#ebedef', '#f2f3f5', '#e3e5e8', '#eceef0', '#f2f3f5', '#ffffff', '#dcdee2'],
    ov: 1.1,
  },
  oled: {
    id: 'oled',
    label: 'OLED black',
    base: 'dark',
    colors: { background: '#000000', surface: '#000000', panel: '#0a0a0a', text: '#ffffff', textMuted: '#b5bac1', accent: '#5865f2', link: '#8db4ff' },
    levels: ['#000000', '#0a0a0a', '#0d0d0d', '#000000', '#080808', '#0a0a0a', '#000000', '#262626'],
    ov: 1,
  },
  contrast: {
    id: 'contrast',
    label: 'High contrast',
    base: 'dark',
    colors: { background: '#000000', surface: '#000000', panel: '#0b0b0b', text: '#ffffff', textMuted: '#ececec', accent: '#8c9bff', link: '#a8d1ff', border: '#9a9a9a' },
    levels: ['#000000', '#000000', '#0b0b0b', '#000000', '#121212', '#0b0b0b', '#000000', '#5c5c5c'],
    ov: 1.8,
    focus: '#ffd400',
  },
};

export const THEME_MODE_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'oled', label: 'OLED black' },
  { value: 'contrast', label: 'High contrast' },
  { value: 'system', label: 'Match system' },
  { value: 'custom', label: 'Custom' },
];

// ---------- colour helpers ----------
export function hexToRgb(hex) {
  const h = normHex(hex);
  if (!h) return null;
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

export function rgbToHex([r, g, b]) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function mix(a, b, t) {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  if (!x || !y) return normHex(a) || normHex(b) || '#000000';
  return rgbToHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}

function triplet(hex) {
  const rgb = hexToRgb(hex);
  return rgb ? rgb.join(',') : '0,0,0';
}

// Black or white, whichever reads better on the given colour.
export function readableOn(hex) {
  return contrastRatio('#ffffff', hex) >= contrastRatio('#111111', hex) ? '#ffffff' : '#111111';
}

export function prefersDark() {
  try {
    return !window.matchMedia || window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return true;
  }
}

export function prefersReducedMotion() {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
}

// Builds the 8 surface levels for a custom theme from its background / panel / surface colours.
function deriveLevels(base, c) {
  if (base === 'light') {
    return [mix(c.background, '#000000', 0.06), mix(c.surface, c.background, 0.55), mix(c.surface, c.background, 0.3), c.background, mix(c.background, c.panel, 0.5), c.panel, c.surface, mix(c.surface, c.text, 0.13)];
  }
  return [mix(c.background, '#000000', 0.6), mix(c.background, '#000000', 0.38), mix(c.background, '#000000', 0.2), c.background, mix(c.background, c.panel, 0.5), c.panel, c.surface, mix(c.surface, c.text, 0.07)];
}

// Resolves a built-in id or a custom theme object into a complete description.
export function resolveTheme(modeOrTheme, { accent = null } = {}) {
  let resolved;
  if (modeOrTheme && typeof modeOrTheme === 'object') {
    const t = sanitizeTheme(modeOrTheme) || { base: 'dark', colors: {} };
    const baseTheme = BUILT_IN_THEMES[t.base === 'light' ? 'light' : 'dark'];
    const colors = { ...baseTheme.colors, ...t.colors };
    resolved = {
      id: 'custom',
      label: t.name || 'Custom',
      base: t.base,
      colors,
      levels: deriveLevels(t.base, colors),
      ov: t.contrast === 'high' ? 1.6 : baseTheme.ov,
      gradient: t.gradient,
      focus: t.contrast === 'high' ? '#ffd400' : null,
      custom: true,
    };
  } else {
    const id = modeOrTheme === 'system' ? (prefersDark() ? 'dark' : 'light') : BUILT_IN_THEMES[modeOrTheme] ? modeOrTheme : 'dark';
    resolved = { ...BUILT_IN_THEMES[id], colors: { ...BUILT_IN_THEMES[id].colors }, gradient: null };
  }
  const a = normHex(accent);
  if (a) resolved.colors = { ...resolved.colors, accent: a };
  return resolved;
}

export function gradientCss(g) {
  if (!g || !Array.isArray(g.stops) || g.stops.length < 2) return '';
  const stops = g.stops.map(normHex).filter(Boolean);
  if (stops.length < 2) return '';
  if (g.type === 'radial') return `radial-gradient(circle at 30% 20%, ${stops.join(', ')})`;
  const angle = Math.max(0, Math.min(360, Math.round(Number(g.angle) || 0)));
  return `linear-gradient(${angle}deg, ${stops.join(', ')})`;
}

// CSS custom properties for a resolved theme. The untouched dark theme returns only the semantic aliases
// used by the new personalisation components, so existing styles keep their exact original colours.
export function buildThemeVars(theme) {
  const t = theme || resolveTheme('dark');
  const c = t.colors;
  const vars = {};
  const pristineDark = t.id === 'dark' && c.accent === BUILT_IN_THEMES.dark.colors.accent;
  if (!pristineDark) {
    if (t.id !== 'dark') {
      vars['--vx-fg-rgb'] = triplet(c.text);
      vars['--vx-ov'] = String(t.ov || 1);
      vars['--vx-text'] = c.text;
      vars['--vx-text-2'] = c.textMuted;
      vars['--vx-link'] = c.link;
      vars['--vx-link-rgb'] = triplet(c.link);
      (t.levels || DARK_LEVELS).forEach((hex, i) => {
        vars[`--vxs-${i}`] = hex;
        vars[`--vxs-${i}-rgb`] = triplet(hex);
      });
    }
    vars['--vx-accent'] = c.accent;
    vars['--vx-accent-rgb'] = triplet(c.accent);
    vars['--vx-on-accent'] = readableOn(c.accent);
  }
  // Semantic aliases (always set) for components written against the token system.
  vars['--vx-bg'] = c.background;
  vars['--vx-surface'] = c.surface;
  vars['--vx-surface-rgb'] = triplet(c.surface);
  vars['--vx-panel'] = c.panel;
  vars['--vx-panel-rgb'] = triplet(c.panel);
  vars['--vx-muted'] = c.textMuted;
  vars['--vx-focus'] = t.focus || c.accent;
  vars['--vx-elevated'] = t.base === 'light' ? '#ffffff' : (t.levels || DARK_LEVELS)[1];
  // Shade strength for black overlays written as rgba(0,0,0, calc(a * var(--vx-sh, 1))): full on dark themes,
  // light on light themes (otherwise nested panels turn a muddy grey).
  vars['--vx-sh'] = t.base === 'light' ? '0.28' : '1';
  if (c.sidebar) vars['--vx-sidebar-bg'] = c.sidebar;
  if (c.border) vars['--vx-border'] = c.border;
  if (c.messageBg) vars['--vx-msg-bg'] = c.messageBg;
  if (c.hover) vars['--vx-hover'] = c.hover;
  if (c.selected) vars['--vx-selected'] = c.selected;
  const grad = gradientCss(t.gradient);
  if (grad) vars['--vx-app-bg'] = grad;
  return vars;
}

// Every property name this module may set, so switching themes removes stale ones.
export const ALL_THEME_VARS = (() => {
  const names = ['--vx-fg-rgb', '--vx-ov', '--vx-text', '--vx-text-2', '--vx-link', '--vx-link-rgb', '--vx-accent', '--vx-accent-rgb', '--vx-on-accent', '--vx-bg', '--vx-surface', '--vx-surface-rgb', '--vx-panel', '--vx-panel-rgb', '--vx-muted', '--vx-focus', '--vx-elevated', '--vx-sidebar-bg', '--vx-border', '--vx-msg-bg', '--vx-hover', '--vx-selected', '--vx-app-bg', '--vx-sh'];
  for (let i = 0; i < 8; i += 1) names.push(`--vxs-${i}`, `--vxs-${i}-rgb`);
  return names;
})();

export function applyThemeVars(vars, el = typeof document !== 'undefined' ? document.documentElement : null) {
  if (!el) return;
  for (const name of ALL_THEME_VARS) {
    if (Object.prototype.hasOwnProperty.call(vars, name)) el.style.setProperty(name, vars[name]);
    else el.style.removeProperty(name);
  }
}

export function clearThemeVars(el = typeof document !== 'undefined' ? document.documentElement : null) {
  if (!el) return;
  for (const name of ALL_THEME_VARS) el.style.removeProperty(name);
}

// ---------- import / export ----------
export const THEME_FILE_FORMAT = 'voxemera-theme';

export function exportThemeFile(theme) {
  const t = sanitizeTheme(theme);
  return JSON.stringify({ format: THEME_FILE_FORMAT, version: SCHEMA_VERSION, theme: t }, null, 2);
}

// Parses an imported theme file strictly. Returns { ok, theme, errors, accessibility }.
export function parseThemeFile(text) {
  const raw = String(text == null ? '' : text);
  if (raw.length > 8192) return { ok: false, errors: ['The file is too large to be a theme'] };
  let doc;
  try {
    doc = JSON.parse(raw);
  } catch {
    return { ok: false, errors: ['The file is not valid JSON'] };
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return { ok: false, errors: ['The file does not contain a theme'] };
  const extra = Object.keys(doc).filter((k) => !['format', 'version', 'theme'].includes(k));
  if (doc.format !== THEME_FILE_FORMAT) return { ok: false, errors: ['This is not a Voxemera theme file'] };
  if (doc.version !== SCHEMA_VERSION) return { ok: false, errors: [`Unsupported theme file version ${JSON.stringify(doc.version)}`] };
  if (extra.length) return { ok: false, errors: extra.map((k) => `Unknown field "${k}"`) };
  return validateThemeStrict(doc.theme);
}

// A plain theme object from a built-in, for "start from" in the editor.
export function themeFromBuiltIn(id, name) {
  const b = BUILT_IN_THEMES[id] || BUILT_IN_THEMES.dark;
  return sanitizeTheme({ name: name || `${b.label} copy`, base: b.base, colors: { ...b.colors }, contrast: id === 'contrast' ? 'high' : 'normal' });
}

export { themeAccessibility, contrastRatio };
